import { IconButton, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

import { materialMoney } from '../../../utils/materialDisplay';
import {
  CONSUMPTION_COST_TEXTS,
  CONSUMPTION_STATUS_CONFIG,
  CONSUMPTION_STATUSES,
  CONSUMPTION_TABLE_TEXTS as TEXTS,
} from '../constants/consumptionsConstants';
import {
  ActionIconButton,
  GeneralTableBox,
  MaterialArticleChips,
  NoDataSkeleton,
  RowTableCell,
  SortableHeadTableCell,
  StatusChip,
  TableBodySkeleton,
} from '../../../common/components';
import { buildConsumptionColumns } from '../utils/buildConsumptionColumns';

const textSx = { textAlign: 'left' };
const numberSx = { textAlign: 'right' };

const MIN_TABLE_WIDTH = 1280;

/**
 * Listado de consumos de stock con materiales, estado y acciones de detalle e impresión.
 * @param {Object} props
 * @param {boolean} [props.showCosts] Muestra el costo original de materiales.
 * @param {Array<Object>} props.data Consumos ya preparados para mostrar.
 * @param {boolean} props.isLoading Carga en curso.
 * @param {{key: string, direction: string}|null} [props.sort] Orden vigente.
 * @param {Function} [props.onSort] Recibe la clave de la columna a ordenar.
 * @param {boolean} [props.actionsDisabled] Deshabilita las acciones mientras hay otra en curso.
 * @param {Function} props.onView Abre el detalle del consumo.
 * @param {Function} props.onPrint Descarga el comprobante de entrega.
 * @returns {JSX.Element}
 */
const ConsumptionsDataTable = ({
  showCosts = false,
  data,
  isLoading,
  sort = null,
  onSort,
  actionsDisabled = false,
  onView,
  onPrint,
}) => {
  const columns = buildConsumptionColumns(showCosts);

  return (
    <GeneralTableBox fixedLayout minTableWidth={MIN_TABLE_WIDTH}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <SortableHeadTableCell
                key={column.key}
                sortKey={column.sortable ? column.key : undefined}
                sort={sort}
                onSort={onSort}
                sx={column.sx}
              >
                {column.label}
              </SortableHeadTableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableBodySkeleton />
          ) : data && data.length > 0 ? (
            data.map((consumption) => {
              const statusConfig = CONSUMPTION_STATUS_CONFIG[consumption.status];
              const projectLabel = consumption.project_name || CONSUMPTION_COST_TEXTS.noProject;
              return (
                <TableRow key={consumption.uuid} hover>
                  <RowTableCell sx={textSx} title={consumption.consumption_number}>
                    {consumption.consumption_number}
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={consumption.order_number}>
                    {consumption.order_number}
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={projectLabel}>
                    {projectLabel}
                  </RowTableCell>
                  <RowTableCell sx={textSx}>
                    <MaterialArticleChips articles={consumption.articles} />
                  </RowTableCell>
                  {showCosts && (
                    <RowTableCell sx={numberSx}>
                      {materialMoney(consumption.total_cost_ars)}
                    </RowTableCell>
                  )}
                  <RowTableCell sx={textSx} title={consumption.warehouse_label}>
                    {consumption.warehouse_label}
                  </RowTableCell>
                  <RowTableCell sx={textSx}>{consumption.created_at_label}</RowTableCell>
                  <RowTableCell>
                    <StatusChip
                      size="small"
                      color={statusConfig?.color}
                      label={statusConfig?.label || consumption.status_label}
                    />
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={consumption.responsible_label}>
                    {consumption.responsible_label}
                  </RowTableCell>
                  <RowTableCell>
                    <Tooltip title={TEXTS.viewDetail}>
                      <span>
                        <ActionIconButton
                          variant="view"
                          onClick={() => onView?.(consumption)}
                          disabled={actionsDisabled}
                        />
                      </span>
                    </Tooltip>
                    {consumption.status === CONSUMPTION_STATUSES.DELIVERED && (
                      <Tooltip title={TEXTS.printReceipt}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onPrint?.(consumption)}
                            disabled={actionsDisabled}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </RowTableCell>
                </TableRow>
              );
            })
          ) : (
            <NoDataSkeleton message={TEXTS.empty} colSpan={columns.length} />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default ConsumptionsDataTable;
